import { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Loader2, Palette, Check, Square, RectangleVertical } from 'lucide-react';

const STYLES = [
  { value: 'band', label: 'Fascia colore' },
  { value: 'badge', label: 'Etichetta' },
  { value: 'minimal', label: 'Minimal' },
];

const DesignerPanel = ({ mediaUrl, defaultHeadline = '', defaultSubline = '', defaultFormat = 'feed', onApply, onCancel }) => {
  const { API, getAuthHeaders } = useAuth();
  const [format, setFormat] = useState(defaultFormat);
  const [style, setStyle] = useState('band');
  const [headline, setHeadline] = useState(defaultHeadline);
  const [subline, setSubline] = useState(defaultSubline);
  const [showLogo, setShowLogo] = useState(true);
  const [showBrand, setShowBrand] = useState(true);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const base = API.replace('/api', '');

  const generate = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/ai-social/design`, {
        media_url: mediaUrl, format, style, headline: headline || null, subline: subline || null, show_logo: showLogo, show_brand_name: showBrand,
      }, getAuthHeaders());
      setPreview(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nella composizione grafica');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-lg border bg-muted/20 p-3 space-y-3" data-testid="designer-panel">
      <div className="flex items-center gap-2 text-sm font-medium"><Palette className="w-4 h-4 text-primary" /> Designer: logo, colore e titolo sulla foto</div>
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3">
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2">
            <Badge variant={format === 'feed' ? 'default' : 'outline'} className="cursor-pointer gap-1" onClick={() => setFormat('feed')} data-testid="designer-format-feed"><Square className="w-3 h-3" /> Feed 1:1</Badge>
            <Badge variant={format === 'story' ? 'default' : 'outline'} className="cursor-pointer gap-1" onClick={() => setFormat('story')} data-testid="designer-format-story"><RectangleVertical className="w-3 h-3" /> Story 9:16</Badge>
            <span className="w-px bg-border mx-1" />
            {STYLES.map(s => (
              <Badge key={s.value} variant={style === s.value ? 'default' : 'outline'} className="cursor-pointer" onClick={() => setStyle(s.value)} data-testid={`designer-style-${s.value}`}>{s.label}</Badge>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-xs">Titolo sull'immagine</Label>
              <Input value={headline} onChange={e => setHeadline(e.target.value)} placeholder="es. Balayage -20%" maxLength={60} data-testid="designer-headline-input" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Sottotitolo</Label>
              <Input value={subline} onChange={e => setSubline(e.target.value)} placeholder="es. Prenota entro il 30 giugno" maxLength={80} data-testid="designer-subline-input" />
            </div>
          </div>
          <div className="flex flex-wrap gap-4 text-xs">
            <label className="flex items-center gap-1.5 cursor-pointer"><input type="checkbox" checked={showLogo} onChange={e => setShowLogo(e.target.checked)} data-testid="designer-logo-checkbox" /> Logo</label>
            <label className="flex items-center gap-1.5 cursor-pointer"><input type="checkbox" checked={showBrand} onChange={e => setShowBrand(e.target.checked)} data-testid="designer-brand-checkbox" /> Nome del salone</label>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="secondary" onClick={generate} disabled={loading} data-testid="designer-preview-btn">
              {loading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Palette className="w-3.5 h-3.5 mr-1" />} {preview ? 'Rigenera anteprima' : 'Genera anteprima'}
            </Button>
            {preview && (
              <Button size="sm" onClick={() => onApply(preview.url, format)} data-testid="designer-apply-btn"><Check className="w-3.5 h-3.5 mr-1" /> Usa questa grafica</Button>
            )}
            {onCancel && <Button size="sm" variant="ghost" onClick={onCancel} data-testid="designer-cancel-btn">Annulla</Button>}
          </div>
        </div>
        <div className={`rounded-md overflow-hidden border bg-muted mx-auto ${format === 'story' ? 'w-[120px] h-[213px]' : 'w-[180px] h-[180px]'}`}>
          {preview
            ? <img src={`${base}${preview.url}`} alt="Anteprima grafica" className="w-full h-full object-cover" data-testid="designer-preview-img" />
            : <img src={`${base}${mediaUrl}`} alt="" className="w-full h-full object-cover opacity-60" />}
        </div>
      </div>
    </div>
  );
};

export default DesignerPanel;
